import { Fade, Grid, LinearProgress } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";

import { SessionContext } from "../../Context/SessionContext";
import { UserContext } from "../../Context/UserContext";
import { CenterMessageCenter, EnhancedCenterMessage } from "../../Models/CenterMessage";
import { DynamicContentView } from "../../Types/DynamicContentView";
import { useApi } from "../../Utils/useApi";
import BookingWizard from "../BookingWizard/BookingWizard";
import CustomerStepper from "../CustomerStepper/CustomerStepper";
import DuplicateModal from "../DuplicateModal/DuplicateModal";
import DynamicContentPanel from "../DynamicContentPanel/DynamicContentPanel";

const HomeContent: React.FC = () => {
  const {
    apptErrorCenters,
    customer: { centerId },
    duplicateCustomers,
    isSavingCustomer,
    showBookingWizard
  } = useContext(SessionContext);
  const { dynamicContent } = useContext(UserContext);
  const { dynamicContentQuery } = useContext(SessionContext);
  const [dynamicContentView, setDynamicContentView] = useState(
    () => dynamicContent && new DynamicContentView(dynamicContent)
  );
  const [expandDynamicContent, setExpandDynamicContent] = useState(false);

  useEffect(() => {
    if (!dynamicContent) return;
    setDynamicContentView(new DynamicContentView(dynamicContent));
  }, [dynamicContent]);

  useEffect(() => {
    if (!dynamicContentQuery) return;
    setDynamicContentView(oldView =>
      oldView ? DynamicContentView.showContent(oldView, dynamicContentQuery) : oldView
    );
  }, [dynamicContentQuery]);

  // Update Dynamic Content with Center Messages
  const { response: getMessages } = useApi<CenterMessageCenter>({
    method: "get",
    url: `Legacy/V0/centers/${centerId}/messages`,
    valid: Boolean(centerId)
  });
  useEffect(() => {
    if (!getMessages) return;
    setDynamicContentView(oldView => {
      if (!oldView) return oldView;
      const tabs = [...oldView.tabs];
      // if there are center messages, update the tab with the content
      if (getMessages.messages.length) {
        const enhancedMessages = getMessages.messages.map(m => new EnhancedCenterMessage(m));
        if (oldView.tabs[0].title.includes("Center"))
          tabs[0] = {
            ...tabs[0],
            showChildIndex: 0,
            title: `Center (${enhancedMessages.length})`,
            content: EnhancedCenterMessage.buildCentersContent(enhancedMessages)
          };
        else tabs.unshift(EnhancedCenterMessage.buildCentersContentTab(enhancedMessages));
        // set the center tab to opened
        oldView.tabIndex = 0;
      }
      // if no center messages, remove the tab
      else if (tabs[0]?.title.includes("Center")) {
        tabs.splice(0, 1);
        // if not on the centers tab, maintain the current tab open
        if (oldView.tabIndex) oldView.tabIndex--;
      }
      return { ...oldView, tabs };
    });
  }, [getMessages]);

  const showBookingWiz = showBookingWizard && !apptErrorCenters.some(id => id === centerId);
  return (
    <>
      {!!duplicateCustomers.length && <DuplicateModal />}
      <Grid item xs sx={{ height: "100%", display: showBookingWiz ? undefined : "none" }}>
        <BookingWizard />
      </Grid>
      <Grid
        className="h-100"
        item
        xs
        zeroMinWidth
        sx={{ display: showBookingWiz || expandDynamicContent ? "none" : undefined }}
      >
        <Fade in={isSavingCustomer}>
          <LinearProgress />
        </Fade>
        <CustomerStepper />
      </Grid>
      <Grid
        className="h-100"
        item
        xs={2.5}
        md={3}
        zeroMinWidth
        style={{
          paddingBottom: "0px",
          display: showBookingWiz ? "none" : undefined
        }}
      >
        <DynamicContentPanel
          dynamicContentView={dynamicContentView}
          setDynamicContentView={setDynamicContentView}
          toggleMaximize={() => setExpandDynamicContent(state => !state)}
        />
      </Grid>
    </>
  );
};

export default HomeContent;
